import React, { useMemo } from "react";
import { Card, LabeledData, Flex, LoadingIndicator, Text, Link } from "@jsq/ds/core";
import {
  useGetFundAdminServicesQuery,
  useGetCanopyFundAdminServicesQuery,
  useGetFundAdministrationRequestIdQuery,
} from "#graphql/supergraphHooks";
import { ControlTowerContext } from "#utils/context";
import { formatFullDateAndTime } from "../../../../utils/datetime";
import { useControlTowerContext } from "../../ControlTowerContext";
import DataTable from "./DataTable";
import DocumentDetailsCard from "./DocumentsDetailsCard";
import ObjectDetailsCard from "./ObjectDetailsCard";
import * as styles from "./styles_vanilla.css";
import { type DetailCategory } from "./types";

export interface RequestDetailsTabProps {
  issueKey: string;
}

const parseDetails = (detailsString?: string | null): DetailCategory[] => {
  if (!detailsString) return [];
  try {
    return JSON.parse(detailsString) as DetailCategory[];
  } catch {
    return [];
  }
};

export const RequestDetailsTab: React.FC<RequestDetailsTabProps> = ({ issueKey }) => {
  const context = useControlTowerContext();
  const isGPX = context === ControlTowerContext.GPX;
  const isCanopy = context === ControlTowerContext.CANOPY;

  const {
    data: requestIdData,
    loading: requestIdLoading,
    error: requestIdError,
  } = useGetFundAdministrationRequestIdQuery({
    variables: { issueKey },
  });

  const requestId = requestIdData?.fundAdministrationRequestId?.requestId;
  const skipQueries = !requestId;

  const {
    data: gpData,
    loading: gpLoading,
    error: gpError,
  } = useGetFundAdminServicesQuery({
    variables: { serviceIds: [requestId || ""] },
    skip: isCanopy || skipQueries,
  });

  const {
    data: canopyData,
    loading: canopyLoading,
    error: canopyError,
  } = useGetCanopyFundAdminServicesQuery({
    variables: { serviceIds: [requestId || ""] },
    skip: !isCanopy || skipQueries,
  });

  const data = useMemo(() => {
    if (isCanopy && canopyData) {
      return {
        fundAdminServices: canopyData.canopyFundAdminServices,
      };
    }
    return gpData ?? null;
  }, [isCanopy, canopyData, gpData]);

  const loading = isCanopy ? canopyLoading : gpLoading;
  const error = isCanopy ? canopyError : gpError;

  const service = data?.fundAdminServices?.edges?.[0]?.node;
  const detailCategories = parseDetails(service?.details);

  const normalizedDocuments = useMemo(() => {
    if (!service?.documents) return [];
    if (isCanopy && canopyData) {
      return canopyData.canopyFundAdminServices!.edges[0].node.documents.map((doc) => ({
        id: doc.id,
        documentType: doc.documentType,
        documentVersion: doc.documentVersion
          ? {
              name: doc.documentVersion.name,
              downloadUrl: doc.documentVersion.directDownloadUrl,
            }
          : null,
      }));
    }
    return gpData!.fundAdminServices!.edges[0].node.documents.map((doc) => ({
      id: doc.id,
      documentType: doc.documentType,
      documentVersion: doc.documentVersion
        ? { name: doc.documentVersion.name, downloadUrl: doc.documentVersion.downloadUrl }
        : null,
    }));
  }, [isCanopy, canopyData, gpData, service?.documents]);

  const normalizedObjects = useMemo(() => {
    if (isCanopy && canopyData) {
      return (canopyData.canopyFundAdminServices?.edges?.[0]?.node?.objects ?? []).map(
        (obj) => {
          if (obj.objectValue.__typename !== "EntityCollectionObjectType") {
            return obj;
          }
          const { entitiesFromCanopy, ...restValue } = obj.objectValue;
          return {
            ...obj,
            objectValue: { ...restValue, entities: entitiesFromCanopy },
          };
        },
      );
    }
    return gpData?.fundAdminServices?.edges?.[0]?.node?.objects ?? [];
  }, [isCanopy, canopyData, gpData]);

  const additionalDocuments = normalizedDocuments.filter(
    (document) => document.documentType === "additional",
  );
  const requiredDocuments = normalizedDocuments.filter(
    (document) => document.documentType !== "additional",
  );
  const hasRequiredDocuments = Boolean(requiredDocuments?.length);

  const detailCategoriesWithoutRequestDetails = detailCategories.filter(
    (category) => category.label.toLowerCase() !== "request details",
  );

  const requestDetailsCategory = detailCategories.find(
    (category) => category.label.toLowerCase() === "request details",
  );

  const isLoading = requestIdLoading || loading;
  const hasError = requestIdError || error;
  const isEmpty = !requestId || !service;

  const detailsUrl = useMemo(() => {
    if (!requestId || !isGPX) return null;
    return `${window.location.origin}/requested-service/${requestId}`;
  }, [requestId, isGPX]);

  return (
    <div className={styles.tabContentContainer}>
      {isLoading && (
        <Flex alignItems="center" justifyContent="center" className={styles.loadingContainer}>
          <LoadingIndicator size="medium" data-testid="request-details-loading-indicator" />
        </Flex>
      )}
      {hasError && <Text variant="regular-md">Error fetching request details</Text>}
      {!isLoading && !hasError && isEmpty && (
        <Text variant="regular-md">No request details</Text>
      )}
      {!isLoading && !hasError && !isEmpty && (
        <>
          <div className={styles.cardContainer}>
            <Card title="Request details" data-testid="request-details-card">
              {service.createdAt && (
                <LabeledData
                  label="Request created"
                  value={formatFullDateAndTime(service.createdAt)}
                  variant="row"
                />
              )}
              {(service.modifiedAt || service.createdAt) && (
                <LabeledData
                  label="Last updated"
                  value={formatFullDateAndTime(service.modifiedAt ?? service.createdAt)}
                  variant="row"
                />
              )}
              {isGPX && detailsUrl && (
                <LabeledData
                  label="More details"
                  value={
                    <Link
                      href={detailsUrl}
                      shouldOpenInNewTab
                      icon="external-link"
                      iconPosition="right"
                      iconVariant="outline"
                      iconSize="small"
                    >
                      View
                    </Link>
                  }
                  variant="row"
                />
              )}
              {requestDetailsCategory?.items?.map((item) => (
                <LabeledData
                  key={item.label}
                  label={item.label}
                  value={item.value as string}
                  variant={item.type === "multi-line-text" ? "standard" : "row"}
                />
              ))}
            </Card>
          </div>

          {detailCategoriesWithoutRequestDetails.map((categoryDetails: DetailCategory) => (
            <div key={categoryDetails.label} className={styles.cardContainer}>
              <Card title={categoryDetails.label}>
                {categoryDetails.items.map((item, index) =>
                  item.type === "table" && Array.isArray(item.value) ? (
                    <div key={`${item.label}${index}`}>
                      <DataTable rows={item.value} />
                    </div>
                  ) : (
                    <LabeledData
                      key={item.label}
                      label={item.label}
                      value={item.value as string}
                      variant={item.type === "multi-line-text" ? "standard" : "row"}
                    />
                  ),
                )}
              </Card>
            </div>
          ))}

          {normalizedObjects.length > 0 && (
            <div className={styles.cardContainer}>
              <ObjectDetailsCard
                requestType={service.requestType ?? "Request"}
                objects={
                  normalizedObjects as Parameters<typeof ObjectDetailsCard>[0]["objects"]
                }
              />
            </div>
          )}

          {hasRequiredDocuments && (
            <div className={styles.cardContainer}>
              <DocumentDetailsCard
                cardTitle="Required documents"
                documentList={requiredDocuments}
              />
            </div>
          )}

          <div className={styles.cardContainer}>
            <DocumentDetailsCard
              cardTitle="Relevant documents"
              documentList={additionalDocuments}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default RequestDetailsTab;
